import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { StyledHtmlRenderer } from './styles';

// Allowed tags for basic text formatting
const ALLOWED_TAGS = [
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'mark',
  'small',
  'sup',
  'sub',
  'p',
  'div',
  'span',
  'br',
  'a',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'code',
  'pre',
  'blockquote',
];

// Allowed attributes per tag
const ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'style'];

// Configure DOMPurify
const purifyConfig = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  ALLOW_DATA_ATTR: false,
};

// Add hook to enforce safe link attributes
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  // Set external links to open in new tab with security attributes
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    const href = node.getAttribute('href');
    if (href && href.startsWith('http')) {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
});

/**
 * HtmlRenderer Component
 *
 * A safe HTML renderer that supports basic text formatting elements.
 * Renders HTML strings with support for common formatting tags like <b>, <i>, <u>, <a>, etc.
 *
 * @param {string} html - The HTML string to render
 * @param {string} className - Optional CSS class name for styling
 * @param {object} style - Optional inline styles
 *
 * @example
 * <HtmlRenderer html="This is <b>bold</b> and <i>italic</i> text" />
 *
 * Supported HTML tags:
 * - Text formatting: <b>, <strong>, <i>, <em>, <u>, <s>, <mark>, <small>, <sup>, <sub>
 * - Structure: <p>, <div>, <span>, <br>
 * - Lists: <ul>, <ol>, <li>
 * - Links: <a>
 * - Headings: <h1>, <h2>, <h3>, <h4>, <h5>, <h6>
 * - Code: <code>, <pre>
 */
const HtmlRenderer = (props) => {
  const { html, className, style } = props;

  const sanitizedHtml = html && typeof html === 'string'
    ? DOMPurify.sanitize(html, purifyConfig)
    : '';

  return React.createElement(StyledHtmlRenderer, {
    className,
    style,
    dangerouslySetInnerHTML: { __html: sanitizedHtml },
  });
};

HtmlRenderer.propTypes = {
  html: PropTypes.string.isRequired,
  className: PropTypes.string,
  style: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};

HtmlRenderer.defaultProps = {
  className: '',
  style: {},
};

export default HtmlRenderer;
